import React from 'react';

type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'blockquote'; text: string }
  | { type: 'code'; language: string; text: string }
  | { type: 'hr' };

const headingClass: Record<number, string> = {
  1: 'text-2xl font-semibold tracking-tight text-zinc-950',
  2: 'text-xl font-semibold tracking-tight text-zinc-950',
  3: 'text-lg font-semibold tracking-tight text-zinc-950',
  4: 'text-base font-semibold text-zinc-950',
  5: 'text-sm font-semibold uppercase tracking-wide text-zinc-700',
  6: 'text-xs font-semibold uppercase tracking-wide text-zinc-600'
};

function isSpecialBlockStart(line: string) {
  return (
    /^#{1,6}\s+/.test(line) ||
    /^```/.test(line) ||
    /^[-*_]\s*$/.test(line) ||
    /^[-*+]\s+/.test(line) ||
    /^\d+\.\s+/.test(line) ||
    /^>\s?/.test(line)
  );
}

function parseMarkdown(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = line.trim();
    if (!trimmed) continue;

    const fence = trimmed.match(/^```(.*)$/);
    if (fence) {
      const language = fence[1]?.trim() || '';
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }
      blocks.push({ type: 'code', language, text: codeLines.join('\n') });
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2].trim() });
      continue;
    }

    if (/^[-*_]\s*$/.test(trimmed)) {
      blocks.push({ type: 'hr' });
      continue;
    }

    if (/^[-*+]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^[-*+]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*+]\s+/, '').trim());
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, '').trim());
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'ol', items });
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      const parts: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
        parts.push(lines[index].trim().replace(/^>\s?/, '').trim());
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'blockquote', text: parts.join(' ') });
      continue;
    }

    const parts = [trimmed];
    while (index + 1 < lines.length) {
      const next = lines[index + 1].trim();
      if (!next || isSpecialBlockStart(next)) break;
      parts.push(next);
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: parts.join(' ') });
  }

  return blocks;
}

function safeHref(rawHref: string) {
  const href = rawHref.trim();
  if (/^(https?:|mailto:)/i.test(href) || href.startsWith('/')) return href;
  return '#';
}

function linkProps(href: string) {
  if (/^https?:/i.test(href)) {
    return { target: '_blank', rel: 'noreferrer' };
  }
  return {};
}

function trimAutoLink(raw: string) {
  const match = raw.match(/^(.+?)([.,;:!?)]*)$/);
  return {
    href: match?.[1] || raw,
    trailing: match?.[2] || ''
  };
}

function earliestMatch(text: string) {
  const patterns = [
    { type: 'code', match: /`([^`]+)`/.exec(text) },
    { type: 'link', match: /\[([^\]]+)\]\(([^)\s]+)\)/.exec(text) },
    { type: 'bold', match: /\*\*([^*]+)\*\*/.exec(text) },
    { type: 'italic', match: /(?<!\*)\*([^*]+)\*(?!\*)/.exec(text) },
    { type: 'autoLink', match: /\b(?:https?:\/\/|mailto:)[^\s<]+/.exec(text) }
  ].filter((item): item is { type: string; match: RegExpExecArray } => Boolean(item.match));

  return patterns.sort((a, b) => a.match.index - b.match.index)[0] ?? null;
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let rest = text;
  let part = 0;

  while (rest) {
    const found = earliestMatch(rest);
    if (!found) {
      nodes.push(rest);
      break;
    }

    if (found.match.index > 0) {
      nodes.push(rest.slice(0, found.match.index));
    }

    const key = `${keyPrefix}-${part}`;
    if (found.type === 'code') {
      nodes.push(
        <code key={key} className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[0.92em] text-zinc-900">
          {found.match[1]}
        </code>
      );
    } else if (found.type === 'link') {
      const href = safeHref(found.match[2]);
      nodes.push(
        <a key={key} href={href} {...linkProps(href)} className="font-medium text-accent underline underline-offset-2">
          {renderInline(found.match[1], `${key}-link`)}
        </a>
      );
    } else if (found.type === 'bold') {
      nodes.push(
        <strong key={key} className="font-semibold text-zinc-950">
          {renderInline(found.match[1], `${key}-bold`)}
        </strong>
      );
    } else if (found.type === 'italic') {
      nodes.push(
        <em key={key} className="italic">
          {renderInline(found.match[1], `${key}-italic`)}
        </em>
      );
    } else {
      const { href: rawHref, trailing } = trimAutoLink(found.match[0]);
      const href = safeHref(rawHref);
      nodes.push(
        <React.Fragment key={key}>
          <a href={href} {...linkProps(href)} className="font-medium text-accent underline underline-offset-2">
            {rawHref}
          </a>
          {trailing}
        </React.Fragment>
      );
    }

    rest = rest.slice(found.match.index + found.match[0].length);
    part += 1;
  }

  return nodes;
}

export function MarkdownContent(props: { markdown: string }) {
  const blocks = React.useMemo(() => parseMarkdown(props.markdown), [props.markdown]);

  return (
    <div className="space-y-4 text-sm leading-6 text-zinc-800">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const Tag = `h${block.level}` as keyof JSX.IntrinsicElements;
          return (
            <Tag key={index} className={headingClass[block.level]}>
              {renderInline(block.text, `h-${index}`)}
            </Tag>
          );
        }
        if (block.type === 'paragraph') {
          return <p key={index}>{renderInline(block.text, `p-${index}`)}</p>;
        }
        if (block.type === 'ul') {
          return (
            <ul key={index} className="list-disc space-y-1 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item, `ul-${index}-${itemIndex}`)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === 'ol') {
          return (
            <ol key={index} className="list-decimal space-y-1 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item, `ol-${index}-${itemIndex}`)}</li>
              ))}
            </ol>
          );
        }
        if (block.type === 'blockquote') {
          return (
            <blockquote key={index} className="border-l-4 border-zinc-300 pl-4 text-zinc-700">
              {renderInline(block.text, `quote-${index}`)}
            </blockquote>
          );
        }
        if (block.type === 'code') {
          return (
            <pre key={index} className="overflow-x-auto rounded-2xl border border-zinc-200 bg-zinc-950 p-4 text-xs text-zinc-50">
              <code>{block.text}</code>
            </pre>
          );
        }
        return <hr key={index} className="border-zinc-200" />;
      })}
    </div>
  );
}
